<template>
  <div class="inbox-page" :class="device.desktop ? 'pa-6' : 'pa-0'">
    <v-row>
      <v-col cols="12">
        <v-card class="inbox-card animated animate__bounceIn elevation-0">
          <div class="inbox-toolbar">
            <div class="inbox-toolbar__left">
              <div class="inbox-toolbar__icon">
                <v-icon>mdi-inbox-arrow-down</v-icon>
              </div>
              <div>
                <div class="inbox-toolbar__title">Pesan Masuk</div>
                <div class="inbox-toolbar__sub">
                  {{ records.length || 0 }} pesan diterima
                  <span v-if="unreadCount"> · {{ unreadCount }} belum dibaca</span>
                </div>
              </div>
            </div>

            <div class="inbox-toolbar__actions">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                placeholder="Cari pengirim / pesan..."
                single-line
                hide-details
                dense
                outlined
                color="#1f7a57"
                class="inbox-search"
                clearable
              ></v-text-field>

              <v-tooltip bottom color="#0d4a36">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    class="inbox-icon-btn"
                    v-on="on"
                    v-show="page.actions.refresh"
                    :loading="syncing"
                    @click="refreshInbox"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Muat ulang & sinkron WA</span>
              </v-tooltip>

              <v-tooltip bottom color="#0d4a36">
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    class="inbox-icon-btn inbox-icon-btn--danger"
                    v-on="on"
                    v-show="page.actions.bulkdelete"
                    @click="postBulkDeleteRecord"
                  >
                    <v-icon>mdi-delete-sweep-outline</v-icon>
                  </v-btn>
                </template>
                <span>Hapus semua</span>
              </v-tooltip>
            </div>
          </div>

          <!-- Desktop table -->
          <v-data-table
            v-show="device.desktop"
            :headers="headers"
            :items="records"
            :items-per-page="50"
            :page.sync="table.options.page"
            class="inbox-table"
            :loading="loading.table"
            loading-text="Memuat pesan masuk..."
            :search="search"
            hide-default-footer
            @page-count="table.options.pageCount = $event"
          >
            <v-progress-linear
              slot="progress"
              color="#1f7a57"
              height="2"
              indeterminate
            ></v-progress-linear>

            <template v-slot:item.tanggal="{ value }">
              <div class="inbox-time">
                <v-icon x-small class="mr-1" color="#1f7a57">mdi-clock-outline</v-icon>
                {{ value || '—' }}
              </div>
            </template>

            <template v-slot:item.sender_number="{ item }">
              <div class="inbox-sender" :class="{ 'is-unread': !item.is_read }">
                <div class="inbox-sender__mark">
                  <v-icon x-small>mdi-account</v-icon>
                </div>
                <div>
                  <div class="inbox-sender__title">{{ item.sender_number || '—' }}</div>
                  <div class="inbox-sender__hint">Ke: {{ item.recieve_number || '—' }}</div>
                </div>
              </div>
            </template>

            <template v-slot:item.content="{ item }">
              <div
                class="inbox-content"
                :class="{ 'is-unread': !item.is_read }"
                :title="item.content"
              >
                {{ item.content || '—' }}
              </div>
            </template>

            <template v-slot:item.type="{ value }">
              <span class="inbox-type">{{ typeLabel(value) }}</span>
            </template>

            <template v-slot:item.status="{ value }">
              <span class="inbox-status" :class="statusClass(value)">
                <span class="inbox-status__dot"></span>
                {{ statusText(value) }}
              </span>
            </template>

            <template v-slot:item.id="{ item }">
              <div class="inbox-row-actions">
                <v-tooltip bottom color="#0d4a36">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="inbox-action-view"
                      v-on="on"
                      @click="openDetail(item)"
                    >
                      <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Lihat</span>
                </v-tooltip>
                <v-tooltip bottom color="#0d4a36" v-if="!item.is_read">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="inbox-action-read"
                      v-on="on"
                      @click="markAsRead(item.id)"
                    >
                      <v-icon small>mdi-check-all</v-icon>
                    </v-btn>
                  </template>
                  <span>Tandai dibaca</span>
                </v-tooltip>
                <v-tooltip bottom color="#0d4a36">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      class="inbox-action-delete"
                      v-on="on"
                      v-show="page.actions.delete"
                      @click="postDeleteRecord(item.id)"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Hapus</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="inbox-empty">
                <v-icon large color="#8fd6b5">mdi-inbox-arrow-down</v-icon>
                <div class="inbox-empty__title">Belum ada pesan masuk</div>
                <div class="inbox-empty__sub">
                  Pesan yang dikirim ke nomor WhatsApp terhubung akan muncul di sini
                </div>
              </div>
            </template>
          </v-data-table>

          <!-- Mobile cards -->
          <div class="inbox-mobile" v-show="device.mobile">
            <div
              v-for="item in filterItems"
              :key="item.id"
              class="inbox-mobile-card"
              :class="{ 'is-unread': !item.is_read }"
              @click="openDetail(item)"
            >
              <div class="inbox-mobile-card__top">
                <div class="inbox-sender">
                  <div class="inbox-sender__mark">
                    <v-icon x-small>mdi-account</v-icon>
                  </div>
                  <div>
                    <div class="inbox-sender__title">{{ item.sender_number }}</div>
                    <div class="inbox-time mt-1">{{ item.tanggal || '—' }}</div>
                  </div>
                </div>
                <v-menu bottom left origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      class="inbox-menu-btn"
                      @click.stop
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="inbox-menu">
                    <v-list-item @click="openDetail(item)">
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#1f7a57">mdi-eye-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Lihat</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="!item.is_read"
                      @click="markAsRead(item.id)"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#1f7a57">mdi-check-all</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Tandai dibaca</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click="postDeleteRecord(item.id)"
                      v-show="page.actions.delete"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="#c62828">mdi-delete-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>Hapus</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <div class="inbox-mobile-card__content">
                {{ item.content || '—' }}
              </div>

              <div class="inbox-mobile-card__meta">
                <span class="inbox-type">{{ typeLabel(item.type) }}</span>
                <span class="inbox-status" :class="statusClass(item.status)">
                  <span class="inbox-status__dot"></span>
                  {{ statusText(item.status) }}
                </span>
              </div>
            </div>

            <div v-if="!filterItems.length" class="inbox-empty pa-8">
              <v-icon large color="#8fd6b5">mdi-inbox-arrow-down</v-icon>
              <div class="inbox-empty__title">Belum ada pesan masuk</div>
              <div class="inbox-empty__sub">
                Pesan yang dikirim ke nomor WhatsApp terhubung akan muncul di sini
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detail dialog -->
    <v-dialog
      transition="dialog-bottom-transition"
      v-model="detail.show"
      :max-width="device.desktop ? '560px' : '100%'"
      persistent
      :fullscreen="device.mobile"
    >
      <v-card class="inbox-dialog-card">
        <div class="inbox-dialog-header">
          <div class="inbox-dialog-header__copy">
            <v-icon class="mr-2">mdi-message-text-outline</v-icon>
            Detail Pesan Masuk
          </div>
          <v-btn icon dark small @click="closeDetail">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="inbox-dialog-body" v-if="detail.item">
          <div class="inbox-detail-meta">
            <div class="inbox-detail-row">
              <span>Pengirim</span>
              <strong>{{ detail.item.sender_number }}</strong>
            </div>
            <div class="inbox-detail-row">
              <span>Diterima di</span>
              <strong>{{ detail.item.recieve_number }}</strong>
            </div>
            <div class="inbox-detail-row">
              <span>Waktu</span>
              <strong>{{ detail.item.tanggal }}</strong>
            </div>
            <div class="inbox-detail-row">
              <span>Tipe</span>
              <strong>{{ typeLabel(detail.item.type) }}</strong>
            </div>
            <div class="inbox-detail-row">
              <span>Status</span>
              <span class="inbox-status" :class="statusClass(detail.item.status)">
                <span class="inbox-status__dot"></span>
                {{ statusText(detail.item.status) }}
              </span>
            </div>
          </div>

          <div class="inbox-field-label mt-4">Isi Pesan</div>
          <div class="inbox-detail-content">
            {{ detail.item.content || '—' }}
          </div>
        </v-card-text>

        <v-divider class="inbox-divider"></v-divider>
        <v-card-actions class="inbox-dialog-actions">
          <v-btn
            text
            class="inbox-btn-ghost"
            v-if="detail.item && !detail.item.is_read"
            @click="markAsRead(detail.item.id)"
          >
            <v-icon left small>mdi-check-all</v-icon>
            Tandai Dibaca
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="inbox-btn-primary" @click="closeDetail">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import "animate.css";

export default {
  name: "PesanMasuk",
  data: () => ({
    headers: [
      {
        text: "Waktu",
        align: "start",
        sortable: true,
        value: "tanggal",
        width: 170,
      },
      {
        text: "Pengirim",
        align: "start",
        sortable: false,
        value: "sender_number",
        width: 180,
      },
      { text: "Pesan", value: "content", sortable: false },
      {
        text: "Tipe",
        align: "center",
        sortable: false,
        value: "type",
        width: 110,
      },
      {
        text: "Status",
        align: "center",
        sortable: false,
        value: "status",
        width: 140,
      },
      {
        text: "Aksi",
        value: "id",
        width: 130,
        sortable: false,
        align: "center",
      },
    ],
    search: null,
    syncing: false,
    detail: {
      show: false,
      item: null,
    },
  }),
  computed: {
    ...mapState([
      "page",
      "theme",
      "http",
      "device",
      "record",
      "records",
      "loading",
      "event",
      "snackbar",
      "form",
      "table",
      "user",
    ]),
    filterItems() {
      if (!this.search) return this.records || [];
      const q = this.search.toLowerCase();
      return (this.records || []).filter((item) => {
        const sender = (item.sender_number || "").toLowerCase();
        const receiver = (item.recieve_number || "").toLowerCase();
        const content = (item.content || "").toLowerCase();
        return (
          sender.indexOf(q) > -1 ||
          receiver.indexOf(q) > -1 ||
          content.indexOf(q) > -1
        );
      });
    },
    unreadCount() {
      return (this.records || []).filter((item) => !item.is_read).length;
    },
  },
  created() {
    this.setPage({
      crud: true,
      dataUrl: "api/pesan/inbox",
      pagination: false,
      title: "PESAN MASUK",
      subtitle: "Berikut Daftar Seluruh Pesan Masuk Yang Tersedia",
      showtable: true,
      breadcrumbs: [
        {
          text: "Pesan",
          disabled: true,
          href: "",
        },
        {
          text: "Pesan Masuk",
          disabled: false,
          href: "",
        },
      ],
      actions: {
        refresh: true,
        add: false,
        edit: false,
        delete: true,
        bulkdelete: true,
        print: false,
        export: false,
        help: false,
      },
    });
    this.refreshInbox();
  },
  mounted() {},
  methods: {
    ...mapActions([
      "setPage",
      "fetchRecords",
      "postConfirmDelete",
      "postConfrimBulkDelete",
      "setLoading",
      "setRecord",
      "setForm",
    ]),
    async refreshInbox() {
      this.syncing = true;
      try {
        const { data } = await this.http.post("api/pesan/inbox-sync");
        if (data && data.message) {
          this.snackbar.color = data.success === false ? "orange" : "#1f7a57";
          this.snackbar.text = data.message;
          this.snackbar.state = true;
        }
      } catch (error) {
        // tetap lanjut load dari database
      } finally {
        this.syncing = false;
      }
      await this.fetchRecords();
    },
    typeLabel(value) {
      const map = {
        text: "Text",
        image: "Gambar",
        video: "Video",
        audio: "Audio",
        document: "Dokumen",
        location: "Lokasi",
        sticker: "Stiker",
      };
      return map[value] || value || "Text";
    },
    statusText(value) {
      if (!value) return "Belum Dibaca";
      if (typeof value === "object") return value.text || "Belum Dibaca";
      return String(value);
    },
    statusClass(value) {
      const text = (
        typeof value === "object" ? value?.text || value?.color || "" : value || ""
      )
        .toString()
        .toLowerCase();

      if (text.includes("dibaca") || text.includes("read") || text.includes("green")) {
        return "is-read";
      }
      return "is-unread";
    },
    async openDetail(item) {
      this.detail.item = item;
      this.detail.show = true;
      if (!item.is_read) {
        await this.markAsRead(item.id, false);
      }
    },
    closeDetail() {
      this.detail.show = false;
      this.detail.item = null;
    },
    async markAsRead(id, refresh = true) {
      try {
        await this.http.put(`api/pesan/inbox/${id}/read`);
        if (this.detail.item && this.detail.item.id === id) {
          this.detail.item.is_read = true;
          this.detail.item.status = { color: "green", text: "Dibaca" };
        }
        if (refresh) {
          this.fetchRecords();
        } else {
          const row = (this.records || []).find((r) => r.id === id);
          if (row) {
            row.is_read = true;
            row.status = { color: "green", text: "Dibaca" };
          }
        }
      } catch (error) {}
    },
    postDeleteRecord(val) {
      this.postConfirmDelete(val);
    },
    postBulkDeleteRecord() {
      this.postConfrimBulkDelete();
    },
  },
};
</script>

<style scoped>
.inbox-page {
  --gw-ink: #10241c;
  --gw-forest: #0d4a36;
  --gw-leaf: #1f7a57;
  --gw-mint: #8fd6b5;
  --gw-fog: #eef6f1;
  --gw-paper: #f8fbf9;
  --gw-line: #c9ddd3;
  --gw-muted: #5d7268;
}

.inbox-card {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid var(--gw-line);
  background: #fff !important;
}

.inbox-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.15rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.inbox-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.inbox-toolbar__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
}

.inbox-toolbar__icon .v-icon {
  color: #0b3d2e !important;
}

.inbox-toolbar__title {
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.2;
}

.inbox-toolbar__sub {
  margin-top: 0.15rem;
  font-size: 0.75rem;
  color: rgba(244, 251, 247, 0.65);
}

.inbox-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.inbox-search {
  max-width: 280px;
  min-width: 180px;
}

.inbox-search >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 10px !important;
  min-height: 38px !important;
}

.inbox-search >>> fieldset {
  border-color: rgba(143, 214, 181, 0.35) !important;
}

.inbox-search >>> input,
.inbox-search >>> .v-icon {
  color: #f4fbf7 !important;
}

.inbox-search >>> input::placeholder {
  color: rgba(244, 251, 247, 0.55) !important;
}

.inbox-icon-btn {
  color: #f4fbf7 !important;
}

.inbox-icon-btn:hover {
  background: rgba(143, 214, 181, 0.18) !important;
}

.inbox-icon-btn--danger:hover {
  background: rgba(198, 40, 40, 0.2) !important;
}

.inbox-btn-primary {
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5) !important;
  color: #0b3d2e !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}

.inbox-table {
  background: transparent !important;
}

.inbox-table >>> thead th {
  background: var(--gw-fog) !important;
  color: var(--gw-forest) !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--gw-line) !important;
}

.inbox-table >>> tbody td {
  border-bottom: 1px solid var(--gw-line) !important;
  color: var(--gw-ink);
  height: 68px !important;
}

.inbox-table >>> tbody tr:hover {
  background: rgba(143, 214, 181, 0.08) !important;
}

.inbox-time {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: var(--gw-muted);
}

.inbox-sender {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.inbox-sender.is-unread .inbox-sender__title,
.inbox-content.is-unread {
  font-weight: 700;
  color: var(--gw-ink);
}

.inbox-sender__mark {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #b7ebcf, #8fd6b5);
  flex-shrink: 0;
}

.inbox-sender__mark .v-icon {
  color: #0b3d2e !important;
}

.inbox-sender__title {
  font-weight: 600;
  color: var(--gw-ink);
  line-height: 1.2;
}

.inbox-sender__hint {
  margin-top: 0.1rem;
  font-size: 0.72rem;
  color: var(--gw-muted);
}

.inbox-content {
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gw-muted);
  font-size: 0.9rem;
}

.inbox-type {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 8px;
  background: var(--gw-fog);
  border: 1px solid var(--gw-line);
  color: var(--gw-forest);
  font-size: 0.75rem;
  font-weight: 600;
}

.inbox-status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid var(--gw-line);
  background: #fff;
  color: var(--gw-muted);
}

.inbox-status.is-read {
  border-color: rgba(31, 122, 87, 0.25);
  background: rgba(143, 214, 181, 0.2);
  color: var(--gw-forest);
}

.inbox-status.is-unread {
  border-color: rgba(245, 158, 11, 0.3);
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
}

.inbox-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #91a59b;
}

.inbox-status.is-read .inbox-status__dot {
  background: var(--gw-leaf);
  box-shadow: 0 0 0 3px rgba(31, 122, 87, 0.15);
}

.inbox-status.is-unread .inbox-status__dot {
  background: #f59e0b;
}

.inbox-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.1rem;
}

.inbox-action-view,
.inbox-action-read {
  color: var(--gw-leaf) !important;
}

.inbox-action-delete {
  color: #c62828 !important;
}

.inbox-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
}

.inbox-empty__title {
  margin-top: 0.75rem;
  font-family: "Outfit", sans-serif;
  font-weight: 700;
  color: var(--gw-ink);
}

.inbox-empty__sub {
  margin-top: 0.25rem;
  font-size: 0.88rem;
  color: var(--gw-muted);
  max-width: 340px;
}

.inbox-mobile {
  padding: 0.85rem;
  display: grid;
  gap: 0.75rem;
}

.inbox-mobile-card {
  border: 1px solid var(--gw-line);
  border-radius: 12px;
  padding: 0.9rem;
  background: #fff;
  cursor: pointer;
}

.inbox-mobile-card.is-unread {
  border-color: rgba(31, 122, 87, 0.35);
  background: rgba(143, 214, 181, 0.08);
}

.inbox-mobile-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.inbox-mobile-card__content {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 10px;
  background: var(--gw-fog);
  color: var(--gw-ink);
  font-size: 0.9rem;
  line-height: 1.45;
}

.inbox-mobile-card__meta {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.inbox-menu-btn {
  color: var(--gw-forest) !important;
}

.inbox-menu {
  border-radius: 10px !important;
}

.inbox-dialog-card {
  border-radius: 14px !important;
  overflow: hidden;
}

.inbox-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.95rem 1.1rem;
  background:
    radial-gradient(120% 80% at 0% 0%, #1a6b4d 0%, transparent 55%),
    linear-gradient(175deg, #0b3d2e 0%, #0f5c45 45%, #0a3327 100%);
  color: #f4fbf7;
}

.inbox-dialog-header__copy {
  display: flex;
  align-items: center;
  font-family: "Outfit", "Montserrat", sans-serif;
  font-weight: 600;
}

.inbox-dialog-header__copy .v-icon {
  color: var(--gw-mint) !important;
}

.inbox-dialog-body {
  padding: 1.25rem !important;
}

.inbox-detail-meta {
  border: 1px solid var(--gw-line);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  background: var(--gw-paper);
}

.inbox-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid var(--gw-line);
  font-size: 0.88rem;
}

.inbox-detail-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.inbox-detail-row span:first-child {
  color: var(--gw-muted);
}

.inbox-detail-row strong {
  color: var(--gw-ink);
  text-align: right;
}

.inbox-field-label {
  margin-bottom: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--gw-forest);
}

.inbox-detail-content {
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid var(--gw-line);
  background: var(--gw-fog);
  color: var(--gw-ink);
  line-height: 1.55;
  white-space: pre-wrap;
}

.inbox-divider {
  border-color: var(--gw-line) !important;
}

.inbox-dialog-actions {
  padding: 0.9rem 1.1rem !important;
  background: var(--gw-paper);
}

.inbox-btn-ghost {
  color: var(--gw-leaf) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

@media (max-width: 600px) {
  .inbox-toolbar__actions {
    width: 100%;
  }

  .inbox-search {
    max-width: none;
    flex: 1;
    min-width: 0;
  }
}
</style>
